/* eslint-disable space-before-function-paren */
import {
  useState,
  createContext,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from "react";
import { UseMutationResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { ANALYTICS_EVENTS } from "analytics";
import { characterKeys } from "queries";
import { useAnalytics } from "hooks/useAnalytics";
import { useAxios } from "hooks/useAxios";
import { type ScopedRequest, useScopedMutation } from "hooks/useScopedMutation";
import { useCharacterList } from "hooks/useCharacters/useCharacterList";
import { USER_API, toastMsg } from "config";
import {
  ICharacter,
  ILocations,
  IUpdateStripboard,
  StripboardTableBanner,
  StripboardTableDayBreak,
} from "../types";
import { handleToast } from "utils";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { scheduleKeys } from "queries";
import { mergedTagClusterDisplayLabel } from "features/script-editor/lib/merged-tag-provenance";
import { useScheduleAiRunSync } from "hooks/schedule/useScheduleAiRunSync";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import type { QueryLoadFailure } from "components/common/QueryLoadError";

/** The locations API body: `status_code` 200 carries the list; any other code is a failure. */
type LocationsResponse =
  | { status_code?: number; message?: string; data?: ILocations[] | null }
  | undefined;

interface Props {
  children: React.ReactNode;
}

export type StripboardContextType = {
  allCharacters: ICharacter[];
  allCharsWithMergedTags: ICharacter[];
  setAllCharacters: React.Dispatch<React.SetStateAction<ICharacter[]>>;
  isScheduleRerunning: boolean;
  setIsScheduleRerunning: React.Dispatch<React.SetStateAction<boolean>>;
  /** True while schedule AI is PENDING or RERUNNING (shared across all schedule routes). */
  isScheduleAiRunning: boolean;
  scheduleProject: ProjectState | null | undefined;
  daybreaks: StripboardTableDayBreak[];
  setDaybreaks: React.Dispatch<React.SetStateAction<StripboardTableDayBreak[]>>;
  shootingLocations: ILocations[];
  /** Set when the draft's locations failed to load, so pickers never show them as none. */
  shootingLocationsLoadError: QueryLoadFailure | undefined;
  setShootingLocations: React.Dispatch<React.SetStateAction<ILocations[]>>;
  updateStripboard: UseMutationResult<
    any,
    any,
    {
      stripboard_id: string;
      update_data: IUpdateStripboard;
    },
    unknown
  >;
  updateBanner: UseMutationResult<
    any,
    any,
    {
      banner_id: string;
      update_data: Partial<StripboardTableBanner>;
    },
    unknown
  >;
};

export const StripboardContext = createContext<StripboardContextType>({} as StripboardContextType);

type EditScope = { projectId: string; documentId: string; hasValidIds: boolean };

export const StripboardProvider = (props: Props) => {
  const axios = useAxios({ allResponse: true });
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();

  const { projectId, documentId, hasValidIds } = useProjectIdentifiers();
  const {
    currentProject: scheduleProject,
    isScheduleAiRunning,
    isScheduleRerunning: scheduleAiRerunning,
  } = useScheduleAiRunSync();

  const [isScheduleRerunningLocal, setIsScheduleRerunning] = useState(false);
  const isScheduleRerunning = scheduleAiRerunning || isScheduleRerunningLocal;
  const [daybreaks, setDaybreaks] = useState<StripboardTableDayBreak[]>([]);

  /**
   * The draft an edit is made in. The schedule pages stay mounted across a draft switch, and
   * React Query hands an edit each new render's callbacks until it settles (also while it
   * waits offline), so every edit carries this and its callbacks read the draft from there.
   */
  const editScope = useMemo<EditScope>(
    () => ({ projectId: projectId + "", documentId: documentId + "", hasValidIds }),
    [projectId, documentId, hasValidIds]
  );

  const trackScheduleUpdated = (
    target: EditScope,
    surface: "stripboard" | "day_out_of_days" | "shooting_calendar"
  ) => {
    trackEvent(ANALYTICS_EVENTS.SCHEDULE_UPDATED, {
      project_id: target.projectId,
      document_id: target.documentId,
      surface,
    });
  };

  const updateStripboard = useScopedMutation(editScope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<
      { stripboard_id: string; update_data: IUpdateStripboard },
      EditScope
    >): Promise<any> => {
      if (!target.hasValidIds) return Promise.reject("IDs not ready");
      return axios.patch(
        USER_API.updateStripboard(target.projectId, target.documentId, params.stripboard_id),
        params.update_data
      );
    },
    onSuccess: (_response, { scope: target }) => {
      trackScheduleUpdated(target, "stripboard");
    },
    onError: () => {
      handleToast(toastMsg.genericServerErr, "error");
    },
  });

  const updateBanner = useScopedMutation(editScope, {
    mutationFn: ({
      variables: params,
      scope: target,
    }: ScopedRequest<
      { banner_id: string; update_data: Partial<StripboardTableBanner> },
      EditScope
    >): Promise<any> => {
      if (!target.hasValidIds) return Promise.reject("IDs not ready");
      return axios.patch(
        USER_API.updateOrDeleteBanner(target.projectId, target.documentId, params.banner_id),
        params.update_data
      );
    },
    onSuccess: (_response, { scope: target }) => {
      trackScheduleUpdated(target, "stripboard");
    },
    onError: () => {
      handleToast(toastMsg.genericServerErr, "error");
    },
  });

  // useCharacterList owns the shared character list key; reading it keeps one parser per key.
  const allCharacters = useCharacterList().characters as unknown as ICharacter[];
  const setAllCharacters = useCallback<Dispatch<SetStateAction<ICharacter[]>>>(
    (action) => {
      queryClient.setQueryData<ICharacter[]>(
        characterKeys.list(projectId || 0, documentId || ""),
        (previous) => (typeof action === "function" ? action(previous ?? []) : action)
      );
    },
    [queryClient, projectId, documentId]
  );

  // The query cache owns the locations, so a remount within the fresh window still shows them.
  // Writes go to the key of the draft that was open when the setter was read.
  // A failed request stays an error, so the location pickers never read it as "No locations yet".
  const locationsQuery = useQuery<ILocations[]>({
    queryKey: scheduleKeys.locations(projectId + "", (documentId || "") + ""),
    queryFn: async () => {
      const locationsBody: LocationsResponse = (
        await axios.get(USER_API.getShootingLocations(projectId + "", documentId + ""))
      ).data;
      if (locationsBody?.status_code !== 200) {
        throw new Error(locationsBody?.message || "Couldn't load shooting locations");
      }
      return Array.isArray(locationsBody.data) ? locationsBody.data : [];
    },
    enabled: hasValidIds,
  });
  const shootingLocations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  // Only a failure with nothing loaded is reported; a failed refresh keeps the locations shown.
  const refetchLocations = locationsQuery.refetch;
  const shootingLocationsLoadError = useMemo<QueryLoadFailure | undefined>(
    () =>
      locationsQuery.isError && locationsQuery.data === undefined
        ? {
            message: "Couldn't load shooting locations",
            onRetry: () => void refetchLocations(),
            isRetrying: locationsQuery.isFetching,
          }
        : undefined,
    [locationsQuery.isError, locationsQuery.data, locationsQuery.isFetching, refetchLocations]
  );
  const setShootingLocations = useCallback<Dispatch<SetStateAction<ILocations[]>>>(
    (action) => {
      queryClient.setQueryData<ILocations[] | undefined>(
        scheduleKeys.locations(projectId + "", (documentId || "") + ""),
        (previous) => (typeof action === "function" ? action(previous ?? []) : action)
      );
    },
    [queryClient, projectId, documentId]
  );

  const mergedTagGroups = useMemo(() => {
    const mergedTagMap = new Map<string, ICharacter[]>();
    for (const char of allCharacters) {
      if (char.tag_merged_label) {
        if (mergedTagMap.has(char.tag_merged_label)) {
          const existing = mergedTagMap.get(char.tag_merged_label);
          if (existing) {
            mergedTagMap.set(char.tag_merged_label, existing.concat(char));
          } else {
            mergedTagMap.set(char.tag_merged_label, [char]);
          }
        } else {
          mergedTagMap.set(char.tag_merged_label, [char]);
        }
      }
    }

    return mergedTagMap;
  }, [allCharacters]);

  const allCharsWithMergedTags = useMemo(() => {
    const tagGroups: ICharacter[] = [];

    for (const chars of Array.from(mergedTagGroups.values())) {
      const first = chars[0]!;
      tagGroups.push({
        ...first,
        name: mergedTagClusterDisplayLabel(first.tag_merged_label, first.name),
      });
    }

    return allCharacters
      .filter((char) => !char.tag_merged_label)
      .concat(tagGroups)
      .sort((a, b) => a.document_character_id - b.document_character_id);
  }, [allCharacters, mergedTagGroups]);

  const updateStripboardRef = useRef(updateStripboard);
  const updateBannerRef = useRef(updateBanner);

  useEffect(() => {
    updateStripboardRef.current = updateStripboard;
  }, [updateStripboard]);

  useEffect(() => {
    updateBannerRef.current = updateBanner;
  }, [updateBanner]);

  const contextValue = useMemo(
    () => ({
      allCharacters,
      allCharsWithMergedTags,
      setAllCharacters,
      shootingLocations,
      shootingLocationsLoadError,
      setShootingLocations,
      updateStripboard: updateStripboardRef.current,
      setIsScheduleRerunning,
      isScheduleRerunning,
      isScheduleAiRunning,
      scheduleProject,
      setDaybreaks: setDaybreaks,
      daybreaks: daybreaks,
      updateBanner: updateBannerRef.current,
    }),
    [
      allCharacters,
      allCharsWithMergedTags,
      setAllCharacters,
      shootingLocations,
      shootingLocationsLoadError,
      setShootingLocations,
      isScheduleRerunning,
      isScheduleAiRunning,
      scheduleProject,
      daybreaks,
    ]
  );

  return (
    <StripboardContext.Provider value={contextValue}>{props.children}</StripboardContext.Provider>
  );
};
